import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ExamQuestion {
  id: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string | null;
  correct_answer: string;
  module_number: number;
  part_number: number;
  course_id: string;
  language: string;
  is_active: boolean;
  created_at: string;
}

// Create a bypass function for getting exam questions
const getExamQuestionsForAdmin = async (courseId: string): Promise<ExamQuestion[]> => {
  console.log('Fetching exam questions for course:', courseId);
  
  // First verify we're authenticated
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  // Check if user is admin using the new RLS-safe function
  const { data: isAdminResult, error: adminError } = await supabase
    .rpc('is_admin_bypass_rls');

  if (adminError) {
    console.error('Admin check failed:', adminError);
    throw new Error('Admin verification failed');
  }

  if (!isAdminResult) {
    throw new Error('Admin access required');
  }

  // Fetch exam questions in two batches to bypass PostgREST 1000-row limit
  try {
    console.log('Fetching exam questions in batches...');
    
    // Fetch first batch (0-999)
    const { data: batch1, error: error1 } = await supabase
      .from('exam_questions')
      .select('*')
      .eq('course_id', courseId)
      .order('module_number', { ascending: true })
      .order('part_number', { ascending: true })
      .range(0, 999);

    if (error1) {
      console.error('Error fetching batch 1:', error1);
      throw error1;
    }

    console.log('Batch 1 fetched:', batch1?.length || 0, 'questions');

    // Fetch second batch (1000-1999)
    const { data: batch2, error: error2 } = await supabase
      .from('exam_questions')
      .select('*')
      .eq('course_id', courseId)
      .order('module_number', { ascending: true })
      .order('part_number', { ascending: true })
      .range(1000, 1999);

    if (error2) {
      console.error('Error fetching batch 2:', error2);
      throw error2;
    }

    console.log('Batch 2 fetched:', batch2?.length || 0, 'questions');

    // Combine both batches
    const allQuestions = [...(batch1 || []), ...(batch2 || [])];

    console.log('Successfully fetched exam questions:', allQuestions.length, 'questions');
    return allQuestions as ExamQuestion[];
  } catch (error) {
    console.error('Failed to fetch exam questions:', error);
    throw new Error('Unable to fetch exam questions. Please try again.');
  }
};

export const useExamQuestions = (courseId: string) => {
  return useQuery({
    queryKey: ['examQuestions', courseId],
    queryFn: async () => {
      const data = await getExamQuestionsForAdmin(courseId);
      console.log('[useExamQuestions] Fetched questions count:', data.length);
      console.log('[useExamQuestions] English count:', data.filter(q => q.language === 'English').length);
      console.log('[useExamQuestions] Spanish count:', data.filter(q => q.language === 'Spanish').length);
      return data;
    },
    staleTime: 0, // Always treat as stale - force fresh fetch
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
    retry: (failureCount, error) => {
      console.error('Query failed:', error);
      // Don't retry authentication or permission errors
      if (error?.message?.includes('Admin access required') || 
          error?.message?.includes('Not authenticated') ||
          error?.message?.includes('Admin verification failed')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: !!courseId,
  });
};

export const useCreateExamQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (question: Omit<ExamQuestion, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('exam_questions')
        .insert(question)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['examQuestions', data.course_id] });
      toast.success('Question created successfully');
    },
    onError: (error) => {
      console.error('Error creating question:', error);
      toast.error('Failed to create question');
    },
  });
};

export const useUpdateExamQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ExamQuestion> }) => {
      const { data, error } = await supabase
        .from('exam_questions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['examQuestions', data.course_id] });
      toast.success('Question updated successfully');
    },
    onError: (error) => {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    },
  });
};

export const useDeleteExamQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('exam_questions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['examQuestions'] });
      toast.success('Question deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    },
  });
};
